import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Typography, Button, Table, Space, Input, Rate, Tag, Tooltip, Modal } from "antd";
import { SearchOutlined, PlusOutlined, ReloadOutlined, EnvironmentOutlined, SettingOutlined, EllipsisOutlined, EyeOutlined, EditOutlined, DeleteOutlined, UndoOutlined, MedicineBoxOutlined, FileTextOutlined } from "@ant-design/icons";
import _get from "lodash/get";
import _debounce from "lodash/debounce";
import { ROLES } from "utils/constants";
import {
  getHealthPlaceListRequest,
  setHealthPlaceFilters,
  setSelectedHealthPlaces,
  deleteHealthPlaceRequest,
  restoreHealthPlaceRequest,
} from "providers/HealthPlaceProvider/slice";
import HealthTypeModal from "../HealthTypeModal";
import SimpleDropdown from "components/common/SimpleDropdown";
import "./styles.less";

const { Search } = Input;

const HealthList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [healthTypeModalVisible, setHealthTypeModalVisible] = useState(false);

  const {
    list,
    total,
    currentPage,
    isLoading,
    filters,
    selectedRowKeys,
  } = useSelector((state) => state.healthPlace);

  const roleCurrentUser = useSelector((state) =>
    _get(state, "auth.currentUser.role")
  );

  const isAdminPermission = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(
    roleCurrentUser
  );

  // Load data on mount
  useEffect(() => {
    dispatch(getHealthPlaceListRequest(filters));
  }, [dispatch, filters]);

  // Debounced search
  const handleSearch = _debounce((value) => {
    const newFilters = {
      ...filters,
      search: value,
      page: 1, // Reset to first page when searching
    };
    dispatch(setHealthPlaceFilters(newFilters));
    dispatch(getHealthPlaceListRequest(newFilters));
  }, 500);

  // Handle pagination
  const handleTableChange = (pagination) => {
    const newFilters = {
      ...filters,
      page: pagination.current,
      limit: pagination.pageSize,
    };
    dispatch(setHealthPlaceFilters(newFilters));
    dispatch(getHealthPlaceListRequest(newFilters));
  };

  // Handle row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      dispatch(setSelectedHealthPlaces({ selectedRowKeys: keys, selectedRows: rows }));
    },
  };

  // Refresh data
  const handleRefresh = () => {
    dispatch(getHealthPlaceListRequest(filters));
  };

  // Navigate to detail
  const handleViewDetail = (record) => {
    navigate(`/health/${record._id}`, {
      state: { canGoBack: true },
    });
  };

  // Navigate to create
  const handleCreate = () => {
    navigate("/health/create", {
      state: { canGoBack: true },
    });
  };

  // Table columns
  const columns = [
    {
      title: "ID",
      dataIndex: "healthPlaceId",
      key: "healthPlaceId",
      width: 100,
      render: (id) => <span className="font-weight-bold">#{id}</span>,
    },
    {
      title: "Tên cơ sở",
      dataIndex: "placeName",
      key: "placeName",
      ellipsis: true,
      render: (name, record) => (
        <Button type="link" onClick={() => handleViewDetail(record)}>
          {name}
        </Button>
      ),
    },
    {
      title: "Loại y tế",
      dataIndex: "healthType",
      key: "healthType",
      width: 200,
      render: (healthTypes) => (
        <div>
          {Array.isArray(healthTypes) && healthTypes.length > 0 ? (
            healthTypes.map((type, index) => (
              <Tag color="blue" key={type._id || index} style={{ marginBottom: 4 }}>
                {type.title}
              </Tag>
            ))
          ) : (
            <Tag color="default">N/A</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Địa chỉ",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      render: (address) => (
        <Tooltip title={address}>
          <Space size="small">
            <EnvironmentOutlined />
            <span>{address}</span>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "Điện thoại",
      dataIndex: "phone",
      key: "phone",
      width: 120,
      render: (phone) => phone || "-",
    },
    {
      title: "Cấp cứu 24/7",
      dataIndex: "emergencyService",
      key: "emergencyService",
      width: 120,
      align: "center",
      render: (emergencyService) => (
        emergencyService ? (
          <Tag color="red" icon={<MedicineBoxOutlined />}>Có</Tag>
        ) : (
          <Tag>Không</Tag>
        )
      ),
    },
    {
      title: "Đánh giá",
      dataIndex: "rating",
      key: "rating",
      width: 150,
      sorter: true,
      render: (rating, record) => (
        <Space direction="vertical" size="small">
          <Rate disabled defaultValue={rating} allowHalf />
          <span className="text-muted">
            ({record.totalNumberOfRatings} lượt)
          </span>
        </Space>
      ),
    },
    {
      title: "Khoảng cách",
      dataIndex: "distance",
      key: "distance",
      width: 120,
      render: (distance) =>
        distance ? `${distance.toFixed(2)} km` : "-",
    },
    {
      title: "Trạng thái",
      dataIndex: "isDeleted",
      key: "isDeleted",
      width: 100,
      render: (isDeleted) => (
        <Tag color={isDeleted ? "red" : "green"}>
          {isDeleted ? "Đã xóa" : "Hoạt động"}
        </Tag>
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 80,
      fixed: "right",
      align: "center",
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Chi tiết',
            onClick: () => handleViewDetail(record),
          },
        ];

        if (isAdminPermission) {
          menuItems.push(
            {
              key: 'edit',
              icon: <EditOutlined />,
              label: 'Sửa',
              onClick: () => navigate(`/health/${record._id}/edit`),
            },
            {
              type: 'divider',
              key: 'divider-1',
            }
          );

          if (record.isDeleted) {
            menuItems.push({
              key: 'restore',
              icon: <UndoOutlined />,
              label: 'Khôi phục',
              onClick: () => handleRestore(record._id),
            });
          } else {
            menuItems.push({
              key: 'delete',
              icon: <DeleteOutlined />,
              label: 'Xóa',
              danger: true,
              onClick: () => handleDelete(record._id),
            });
          }
        }

        return (
          <SimpleDropdown items={menuItems}>
            <Button
              type="text"
              icon={<EllipsisOutlined />}
              size="small"
            />
          </SimpleDropdown>
        );
      },
    },
  ];

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Xác nhận xóa",
      content: "Bạn có chắc chắn muốn xóa cơ sở y tế này?",
      okText: "Xóa",
      okType: "danger",
      cancelText: "Hủy",
      onOk: () => {
        dispatch(deleteHealthPlaceRequest({ id }));
        // Refresh list after delete
        setTimeout(() => {
          dispatch(getHealthPlaceListRequest(filters));
        }, 500);
      },
    });
  };

  const handleRestore = (id) => {
    Modal.confirm({
      title: "Xác nhận khôi phục",
      content: "Bạn có chắc chắn muốn khôi phục cơ sở y tế này?",
      okText: "Khôi phục",
      cancelText: "Hủy",
      onOk: () => {
        dispatch(restoreHealthPlaceRequest({ id }));
        // Refresh list after restore
        setTimeout(() => {
          dispatch(getHealthPlaceListRequest(filters));
        }, 500);
      },
    });
  };

  return (
    <div className="health-list-wrapper">
      <Row className="health-list-header" align="middle" justify="space-between">
        <Typography.Title level={4}>Danh sách cơ sở y tế</Typography.Title>
        {isAdminPermission && (
          <Space>
            <Button
              size="large"
              icon={<SettingOutlined />}
              onClick={() => setHealthTypeModalVisible(true)}
            >
              Quản lý loại y tế
            </Button>
            <Button
              size="large"
              icon={<FileTextOutlined />}
              onClick={() => navigate("/health-insurance")}
            >
              Bảo hiểm & Quy trình
            </Button>
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Thêm cơ sở mới
            </Button>
          </Space>
        )}
      </Row>

      <div className="content-container">
        <div className="search-filter-bar">
          <Space size="middle" style={{ width: "100%" }}>
            <Search
              placeholder="Tìm kiếm theo tên, địa chỉ..."
              allowClear
              enterButton={<SearchOutlined />}
              style={{ width: 400 }}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={isLoading}
            >
              Làm mới
            </Button>
          </Space>
        </div>

        <Table
          rowKey="_id"
          rowSelection={isAdminPermission ? rowSelection : undefined}
          columns={columns}
          dataSource={list}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          pagination={{
            current: currentPage,
            pageSize: filters.limit,
            total: total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} cơ sở`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={handleTableChange}
        />
      </div>

      {/* Health Type Modal */}
      <HealthTypeModal
        visible={healthTypeModalVisible}
        onClose={() => setHealthTypeModalVisible(false)}
      />
    </div>
  );
};

export default HealthList;
